import type { PrinterProfile } from '@tsagko/label-contracts'
import { getPrinterProfile, getPrinterProfiles } from '@tsagko/label-printer-profiles'

/**
 * localStorage key the Preview pane's printer-profile selection is
 * persisted under (issue #132). This is the ONLY key any other route
 * should read to stay in sync with whatever profile the operator is
 * currently (or was last) previewing against — e.g.
 * BlockConfiguratorRoute's publish-time width check reads it via
 * `getStoredPreviewProfile` below instead of lifting `profileId` state out
 * of PreviewPane.
 */
export const PREVIEW_PROFILE_STORAGE_KEY = 'label-designer.preview.profileId'

/**
 * try/catch guarded like SplitPane.tsx's readStored/persist pair — a
 * private-mode browser or a full storage quota must not crash the pane.
 */
export function readStoredProfileId(): string | null {
  try {
    return localStorage.getItem(PREVIEW_PROFILE_STORAGE_KEY)
  } catch {
    return null
  }
}

export function persistProfileId(id: string): void {
  try {
    localStorage.setItem(PREVIEW_PROFILE_STORAGE_KEY, id)
  } catch {
    // ignore (private mode, quota)
  }
}

/**
 * The printer-profile id the Preview pane should select: the persisted id
 * when it still names a profile in the current catalogue, else the
 * catalogue's first profile. Profiles can be added/removed across deploys,
 * so a stale stored id (from a previous session, possibly a previous
 * deploy) must not wedge the pane on an id that no longer resolves.
 */
export function resolveStoredProfileId(): string {
  const profiles = getPrinterProfiles()
  const stored = readStoredProfileId()
  if (stored && profiles.some(p => p.id === stored)) return stored
  return profiles[0]?.id ?? ''
}

/**
 * The full PrinterProfile the Preview pane is currently (or was last)
 * configured to render against, resolved from the same persisted id. Used
 * outside PreviewPane by code that needs the profile (e.g. its
 * `usablePrintWidthMm`) without lifting `profileId` state out of the pane.
 */
export function getStoredPreviewProfile(): PrinterProfile | undefined {
  return getPrinterProfile(resolveStoredProfileId())
}
